/**
 * Tree Global Variables 도메인 상수
 */

import type { MessageSchemaStringType } from '@/common/locale';
import type { TreeVariablePathRule, TreeVariablePlatform } from './treeVariable.types';

/**
 * targetFilterType + operationType 조합에 따른 tagType 매핑
 * PATTERN(keyword): pattern / and_pattern
 * MANUAL: manual / and_manual
 */
export const TREE_VARIABLE_TAG_TYPE = {
  PATTERN: { OR: 'pattern', AND: 'and_pattern' },
  MANUAL: { OR: 'manual', AND: 'and_manual' },
} as const;

export const MAXGAUGE_TREE_VARIABLE_PLATFORMS: TreeVariablePlatform[] = [
  'HOST',
  // 'CLOUD',
  'DATABASE',
  // 'BUSINESS',
];

export const TREE_VARIABLE_PLATFORMS: {
  value: TreeVariablePlatform;
  labelKey: MessageSchemaStringType;
}[] = [
  { value: 'HOST', labelKey: 'WORD.HOST' },
  { value: 'NETWORK_DEVICE', labelKey: 'WORD.NETWORK_DEVICE' },
  { value: 'MESSAGE_QUEUE', labelKey: 'WORD.MESSAGE_QUEUE' },
  // { value: 'CLOUD', labelKey: 'WORD.CLOUD' },
  { value: 'KUBERNETES', labelKey: 'WORD.KUBERNETES' },
  { value: 'APPLICATION', labelKey: 'WORD.APPLICATION' },
  { value: 'DATABASE', labelKey: 'WORD.DATABASE' },
  // { value: 'BUSINESS', labelKey: 'WORD.BUSINESS' },
  { value: 'RUM', labelKey: 'WORD.RUM' },
  { value: 'WEBSERVER', labelKey: 'WORD.WEB_SERVER' },
];

export const DEFAULT_OBJECT_TYPES: { value: string; label: string }[] = [
  { value: 'group', label: 'Group' },
  { value: 'list', label: 'List' },
];

export const DEFAULT_OBJECT_TYPE_VALUES = DEFAULT_OBJECT_TYPES.map(({ value }) => value);

/** 플랫폼별 실제 objectType API 값 매핑 (group/list → 플랫폼 고유 값) */
export const PLATFORM_ACTUAL_OBJECT_TYPE_MAP: Record<string, Record<string, string>> = {
  HOST: { group: 'infra_group', list: 'host' },
  NETWORK_DEVICE: { group: 'network_device_group', list: 'network_device' },
  MESSAGE_QUEUE: { group: 'message_queue_group', list: 'kafka_cluster' },
  CLOUD: { group: 'cloud_group', list: 'cloud' },
  APPLICATION: { group: 'application_group', list: 'application' },
  DATABASE: { group: 'database_group', list: 'database' },
  BUSINESS: { group: 'business_group', list: 'business' },
  RUM: { group: 'rum_group', list: 'rum' },
  WEBSERVER: { group: 'webserver_group', list: 'webserver' },
};

export const PLATFORM_OBJECT_TYPES: Record<string, { value: string; label: string }[]> = {
  KUBERNETES: [
    { value: 'cluster', label: 'Cluster' },
    { value: 'node', label: 'Node' },
    { value: 'namespace', label: 'Namespace' },
    { value: 'workload', label: 'Workload' },
    { value: 'pod', label: 'Pod' },
    { value: 'container', label: 'Container' },
  ],
};

/** ObjectType key → label 매핑 */
export const OBJECT_TYPE_LABEL_MAP: Record<string, string> = Object.fromEntries(
  [...DEFAULT_OBJECT_TYPES, ...Object.values(PLATFORM_OBJECT_TYPES).flat()].map(
    ({ value, label }) => [value, label],
  ),
);

export const WORKLOAD_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'deployment', label: 'Deployment' },
  { value: 'statefulset', label: 'StatefulSet' },
  { value: 'daemonset', label: 'DaemonSet' },
  { value: 'replicaset', label: 'ReplicaSet' },
];

export const PATH_RULE_OPTIONS_BY_OBJECT_TYPE: Record<
  string,
  { value: TreeVariablePathRule; label: string }[]
> = {
  pod: [
    { value: 'NAMESPACE', label: 'Namespace' },
    { value: 'NODE', label: 'Node' },
  ],
  container: [
    { value: 'NAMESPACE', label: 'Namespace' },
    { value: 'NODE', label: 'Node' },
  ],
};

/** PathRule에 따른 부모 depth 노드 목록 */
export const PATH_RULE_DEPTHS: Partial<
  Record<TreeVariablePathRule, { key: string; label: string }[]>
> = {
  NAMESPACE: [
    { key: 'cluster', label: 'Cluster' },
    { key: 'namespace', label: 'Namespace' },
    { key: 'workload', label: 'Workload' },
  ],
  NODE: [
    { key: 'cluster', label: 'Cluster' },
    { key: 'node', label: 'Node' },
  ],
};

/** objectType별 고정 부모 계층 */
export const OBJECT_TYPE_DEPTHS: Record<string, { key: string; label: string }[]> = {
  list: [{ key: 'group', label: 'Group' }],
  node: [{ key: 'cluster', label: 'Cluster' }],
  namespace: [{ key: 'cluster', label: 'Cluster' }],
  workload: [
    { key: 'cluster', label: 'Cluster' },
    { key: 'namespace', label: 'Namespace' },
  ],
};
